import {ChangeDetectionStrategy, Component, Input} from "@angular/core";
import {AdditionalQuestionGroup, BinaryQuestion, Order, Product, StandardName, TextQuestion} from "../../../../store/state/order.state";
import {AdditionalQuestionsChanges, ChangeSet} from "../../history/order-history.model";
import {Observable, of} from "rxjs";
import {map} from "rxjs/operators";
import {groupBy} from "../../../../umbrella-common/common-functions";
import {TranslateService} from "@ngx-translate/core";
import {TranslationsHelperService} from "../../../translations/translations-helper.service";

@Component({
    selector: 'base-additional-questions-details',
    templateUrl: './base-additional-questions-details.template.html',
    styleUrls: ['./base-additional-questions-details.style.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BaseAdditionalQuestionsDetailsComponent {

	readonly RANDOM_NUMBER: number = Math.random();
	readonly OUTSOURCED_PROCESSES_KEY_BASE = "additional-questions.outsources-processes.";
	readonly predefinedOutsourcedProcessesKeys: string[] = ['none', 'design', 'manufacturing', 'sales'];

	@Input() order: Order;
	@Input() product: Product;
	@Input() changeSet: ChangeSet;
	@Input() translateClientsData;
	@Input() useOriginalTranslations: boolean;

	constructor(private translateService: TranslateService, public translationsHelper: TranslationsHelperService) {
	}

	areOutsourcedProcessesChanged(): boolean {
		return !!this.changeSet && this.additionalQuestionsChangesPresent() && !!this.additionalQuestionChanges().outsourcedProcesses;
	}

	isBinaryQuestionChanged(question: BinaryQuestion): boolean {
		if (!this.changeSet || !this.additionalQuestionsChangesPresent()) {
			return false;
		}

		const change = this.additionalQuestionChanges().binaryQuestionChanges[question.type];
		return !!change && (!!change.answer || !!change.description);
	}

	isTextQuestionChanged(question: TextQuestion): boolean {
		return !!this.changeSet && this.additionalQuestionsChangesPresent() && !!this.additionalQuestionChanges().textQuestionChanges[question.type] &&
			!!this.additionalQuestionChanges().textQuestionChanges[question.type].description;
	}

	private additionalQuestionChanges(): AdditionalQuestionsChanges {
		return this.changeSet.changes.productChanges[this.product.standard.name].additionalQuestions;
	}

	private additionalQuestionsChangesPresent() {
		return !!this.changeSet.changes.productChanges[this.product.standard.name] && !!this.changeSet.changes.productChanges[this.product.standard.name].additionalQuestions;
	}

	isNotIso27701(): boolean {
		return this.product.standard.name !== StandardName.ISO_27701;
	}

	binaryQuestions(): BinaryQuestion[] {
		if (this.isNotIso27701()) {
			return this.product.additionalQuestions.binaryQuestions;
		}

		const iso27001BinaryQuestions: BinaryQuestion[] = this.order.products.find(p => p.standard.name === StandardName.ISO_27001).additionalQuestions.binaryQuestions;
		return this.product.additionalQuestions.binaryQuestions.filter(binaryQuestion => !iso27001BinaryQuestions.some(bq => bq.type == binaryQuestion.type));
	}

	textQuestions(): Observable<Map<string, TextQuestion[]>> {
		const questions = this.product.additionalQuestions.textQuestions;
		if (this.isNotIso27701()) {
			return of(questions).pipe(
				map(data => {
					const questionsWithoutAnswers = data.filter(question => question.answer != undefined);
					return groupBy(questionsWithoutAnswers, item => item.group);
				})
			);
		}

		const iso27001TextQuestions: TextQuestion[] = this.order.products.find(p => p.standard.name === StandardName.ISO_27001).additionalQuestions.textQuestions;
		const filteredQuestions = questions.filter(textQuestion => !iso27001TextQuestions.some(tq => tq.type == textQuestion.type));

		return of(filteredQuestions).pipe(
			map(data => groupBy(data, item => item.group))
		);
	}

	getGroupName(group: AdditionalQuestionGroup): Observable<string> {
		return this.translateService.get(`additional-questions.group.${group}`);
	}

	standardName(): StandardName {
		return this.product.standard.name;
	}

	getTranslationSubjectForBinaryQuestion(question: BinaryQuestion): string {
		return `additionalQuestions.binaryQuestion[${question.type}]`;
	}

	getTranslationSubjectForTextQuestion(question: TextQuestion): string {
		return `additionalQuestions.textQuestion[${question.type}]`;
	}

	binaryQuestionTranslationVisible(question: BinaryQuestion): boolean {
		return this.translationVisible(this.getTranslationSubjectForBinaryQuestion(question));
	}

	textQuestionTranslationVisible(question: TextQuestion): boolean {
		return this.translationVisible(this.getTranslationSubjectForTextQuestion(question));
	}

	translationVisible(subject: string): boolean {
		return this.translationsHelper.translationExists(this.standardName(), subject, this.useOriginalTranslations);
	}

	getTranslatedText(subject: string): string {
		return this.translationsHelper.getTranslation(this.standardName(), subject, this.useOriginalTranslations);
	}

	getBinaryQuestionTranslatedText(question: BinaryQuestion): string {
		return this.translationsHelper.getTranslation(this.standardName(), this.getTranslationSubjectForBinaryQuestion(question), this.useOriginalTranslations);
	}

	getTextQuestionTranslatedText(question: TextQuestion): string {
		return this.translationsHelper.getTranslation(this.standardName(), this.getTranslationSubjectForTextQuestion(question), this.useOriginalTranslations);
	}

	trackByTextQuestion(index: number, item: any) {
		return item.value.type;
	}

	trackByBinaryQuestion(index: number, item: any) {
		return item.type;
	}

	trackByIndex(index: number) {
		return index;
	}

	translate(process): Observable<string> {
		if (this.predefinedOutsourcedProcessesKeys.includes(process)) {
			return this.translateService.get(this.OUTSOURCED_PROCESSES_KEY_BASE + process);
		} else {
			return of(process);
		}
	}
}
